import { createContext, useState, useCallback, type ReactNode } from "react"
import { signIn as apiSignIn, signUp as apiSignUp, signOut as apiSignOut } from "../api/auth"
import type { User } from "../types"

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<string>
  signup: (
    name: string,
    email: string,
    password: string,
    passwordConfirmation: string,
  ) => Promise<void>
  logout: () => Promise<void>
  isAdmin: boolean
}

export const AuthContext = createContext<AuthContextType | null>(null)

function readStoredUser(): User | null {
  const savedToken = localStorage.getItem("token")
  const savedUser = localStorage.getItem("user")
  if (!savedToken || !savedUser) return null
  try {
    return JSON.parse(savedUser) as User
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => readStoredUser())

  const login = useCallback(async (email: string, password: string): Promise<string> => {
    const { user, token } = await apiSignIn(email, password)
    localStorage.setItem("token", token)
    localStorage.setItem("user", JSON.stringify(user))
    setUser(user)
    return user.role
  }, [])

  const signup = useCallback(
    async (name: string, email: string, password: string, passwordConfirmation: string) => {
      const { user, token } = await apiSignUp(name, email, password, passwordConfirmation)
      localStorage.setItem("token", token)
      localStorage.setItem("user", JSON.stringify(user))
      setUser(user)
    },
    [],
  )

  const logout = useCallback(async () => {
    try {
      await apiSignOut()
    } finally {
      localStorage.removeItem("token")
      localStorage.removeItem("user")
      setUser(null)
    }
  }, [])

  const isAdmin = user?.role === "admin"

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  )
}
